<template>
  <v-container>
    <h1>Intake Ops Review</h1>
    <v-alert v-if="errorMessage" type="error" variant="tonal">
      {{ errorMessage }}
    </v-alert>
    <div v-if="automationsInfo">
      <v-row>
        <v-col cols="12">
          <v-btn-lh :href="automationsInfo.intakeUrl" target="_blank">Go to document intake</v-btn-lh>
        </v-col>
        <v-col cols="12" sm="6">
          <v-alert type="warning" variant="tonal"> Do not edit the intake document. </v-alert>
        </v-col>
      </v-row>
      <v-row class="font-weight-bold">
        <v-col cols="12"> Company: {{ automationsInfo.clientCompany.companyName }} </v-col>
        <v-col cols="12"> Patient: </v-col>
        <v-col cols="12">
          <v-form ref="patientFormRef" action="" validate-on="eager">
            <v-row dense>
              <v-col :cols="formColSize">
                <v-text-field
                  type="text"
                  :readonly="true"
                  :model-value="automationsInfo.patient.firstName"
                  label="First Name">
                </v-text-field>
              </v-col>
              <v-col :cols="formColSize">
                <v-text-field
                  type="text"
                  :readonly="true"
                  :model-value="automationsInfo.patient.lastName"
                  label="Last Name">
                </v-text-field>
              </v-col>
              <v-col :cols="formColSize">
                <v-text-field
                  v-model="currentExternalId"
                  type="text"
                  :readonly="!!automationsInfo.patient.externalId"
                  label="Patient ID (MRN)">
                </v-text-field>
              </v-col>
              <v-col>
                <v-btn-lh v-if="shouldShowUpdateButton" class="mt-3" color="warning" @click="updatePatient">
                  Update Patient ID (MRN)
                </v-btn-lh>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <h2>Automations ({{ automationsInfo?.automationsInfo?.length ?? 0 }})</h2>
        </v-col>
      </v-row>
      <template v-for="automation in automationsInfo.automationsInfo" :key="automation.intakeUrl">
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-col cols="12">
                  {{ automation.type ?? "Submission Job" }}
                </v-col>
              </v-card-title>
              <v-card-text>
                <v-col cols="12">
                  Status:
                  <v-chip :color="statusColors[automation.status.toLowerCase()]" variant="tonal">
                    {{ automation.status }}
                  </v-chip>
                </v-col>
                <v-col v-if="automation.condition" cols="12" sm="6"> Condition: {{ automation.condition }} </v-col>
                <v-col v-if="automation.conditionData" cols="12" sm="6">
                  Condition Data: {{ automation.conditionData }}
                </v-col>
                <v-col cols="12" sm="6"> Created: {{ new Date(automation.createdOn).toLocaleString() }} </v-col>
                <v-col cols="12" sm="6"> Modified: {{ new Date(automation.modifiedOn).toLocaleString() }} </v-col>
                <v-col cols="12">
                  <v-btn-lh :href="automation.adminUrl" target="_blank"> Go to admin </v-btn-lh>
                </v-col>
                <v-col cols="12">
                  Ops Instructions:
                  <!-- eslint-disable vue/no-v-html -->
                  <div
                    v-if="automation.opsInstructions"
                    class="overflow-y-auto overflow-x-auto"
                    v-html="sanitizeHtml(automation.opsInstructions)"></div>
                  <!-- eslint-enable vue/no-v-html -->
                  <div v-else class="overflow-y-auto">None yet</div>
                </v-col>
                <v-col v-if="automation.taskData" cols="12">
                  Task Data:
                  <pre class="overflow-y-auto">
                  {{ automation.taskData || "None yet" }}
                  </pre>
                </v-col>
                <v-col v-if="automation.outcome" cols="12">
                  Outcome:
                  <pre class="overflow-y-auto">
                  {{ automation.outcome || "None yet" }}
                  </pre>
                </v-col>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <br /><br />
      </template>
    </div>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { intakeService } from "@/services/intakeService";
import { patientService } from "@/services/patientService";
import { useResponsive } from "@/composables/useResponsive";
import DOMPurify from "dompurify";
const route = useRoute();
const { isMobile } = useResponsive();
const key = route.params.key;
const automationsInfo = ref(null);
const errorMessage = ref(null);
const patientFormRef = ref(null);
const currentExternalId = ref("");
const isUpdatingExternalId = ref(false);

const shouldShowUpdateButton = computed(() => {
  if (!automationsInfo.value) return false;
  const originalExternalIdIsEmpty = !automationsInfo.value.patient?.externalId;
  const userHasTypedSomething = !!currentExternalId.value;
  return originalExternalIdIsEmpty && userHasTypedSomething && !isUpdatingExternalId.value;
});

const formColSize = computed(() => {
  if (isMobile.value) return 12;
  if (!automationsInfo.value) return 3;
  const originalExternalIdExists = !!automationsInfo.value.patient?.externalId;
  return originalExternalIdExists ? 4 : 3;
});

const statusColors = {
  pending: "warning",
  success: "success",
  failed: "error",
};

const sanitizeHtml = (rawHtml) => {
  if (!rawHtml) return null;
  try {
    return DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: [
        "p",
        "pre",
        "br",
        "strong",
        "em",
        "b",
        "i",
        "u",
        "blockquote",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "a",
        "table",
        "tr",
        "td",
        "tbody",
      ],
      ALLOWED_ATTR: ["class", "id", "href", "target"],
      KEEP_CONTENT: true,
    });
  } catch (_error) {
    return null;
  }
};

const updatePatient = async () => {
  if (isUpdatingExternalId.value) return;
  isUpdatingExternalId.value = true;
  try {
    await patientService.updateExternalId(automationsInfo.value.patient.id, currentExternalId.value);
    automationsInfo.value.patient.externalId = currentExternalId.value;
  } catch (error) {
    errorMessage.value = error.message || "Failed to update Patient ID (MRN)";
    isUpdatingExternalId.value = false;
  }
};

onMounted(async () => {
  try {
    const response = await intakeService.getIntakeAutomationsInfo(key);
    automationsInfo.value = response;
    currentExternalId.value = response.patient?.externalId || "";
  } catch (error) {
    errorMessage.value = error.message || error;
  }
});
</script>

<style scoped>
pre {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
